import React from 'react';
import Card from './Card';

export interface PlotCardProps {
  title?: string;
  subtitle?: string;
  children: React.ReactNode;
  actions?: React.ReactNode;
  tabs?: Array<{
    id: string;
    label: string;
    icon?: string;
  }>;
  activeTab?: string;
  onTabChange?: (tabId: string) => void;
  loading?: boolean;
  loadingComponent?: React.ReactNode;
  className?: string;
  // New optimization props
  compact?: boolean;
  height?: 'sm' | 'md' | 'lg' | 'xl' | 'auto';
  fullHeight?: boolean;
}

const PlotCard: React.FC<PlotCardProps> = ({
  title,
  subtitle,
  children,
  actions,
  tabs,
  activeTab,
  onTabChange,
  loading = false,
  loadingComponent,
  className = '',
  compact = false,
  height = 'lg',
  fullHeight = false,
}) => {
  // Height classes for different sizes
  const heightClasses = {
    sm: 'min-h-80', // 320px
    md: 'min-h-96', // 384px
    lg: 'min-h-[32rem]', // 512px
    xl: 'min-h-[40rem]', // 640px
    auto: '',
  };

  const _plotHeight = fullHeight ? 'h-full' : heightClasses[height];

  return (
    <Card
      variant="plot"
      size="full" // Changed from 'lg' to 'full' to match other cards (p-6)
      hover={false}
      loading={loading && !loadingComponent} // Only use Card loading if no custom component
      className={`${className} ${fullHeight ? 'h-full flex flex-col' : ''}`}
    >
      {/* Compact Header - Optimize space usage */}
      {(title || subtitle || actions || tabs) && (
        <div className="border-b border-gray-200 pb-4 mb-6">
          {/* Title and Actions Row */}
          {(title || subtitle || actions) && (
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                {title && (
                  <h3
                    className={`onyx-subtitle text-gray-900 ${compact ? 'text-base' : 'text-lg'} truncate`}
                  >
                    {title}
                  </h3>
                )}
                {subtitle && (
                  <p
                    className={`onyx-body text-gray-600 ${compact ? 'text-xs mt-0.5' : 'text-sm mt-1'} truncate`}
                  >
                    {subtitle}
                  </p>
                )}
              </div>

              {/* Compact Actions */}
              {actions && (
                <div className={`flex-shrink-0 ${compact ? 'ml-3' : 'ml-4'}`}>
                  <div
                    className={`flex items-center ${compact ? 'gap-2 text-xs' : 'gap-3 text-sm'}`}
                  >
                    {actions}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Compact Tabs */}
          {tabs && tabs.length > 0 && (
            <div className={`${title || subtitle || actions ? (compact ? 'mt-2' : 'mt-4') : ''}`}>
              <nav className={`flex ${compact ? 'space-x-4' : 'space-x-6'}`} aria-label="Plot tabs">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => onTabChange?.(tab.id)}
                    className={`${
                      activeTab === tab.id
                        ? 'border-onyx-bright-blue text-onyx-bright-blue'
                        : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                    } whitespace-nowrap border-b-2 ${
                      compact ? 'py-2 px-1 text-xs' : 'py-3 px-1 text-sm'
                    } onyx-subtitle transition-colors duration-150 ease-in-out flex items-center`}
                  >
                    {tab.icon && (
                      <span className={`${compact ? 'mr-1 text-xs' : 'mr-2'}`}>{tab.icon}</span>
                    )}
                    <span className={compact ? 'truncate max-w-20' : ''}>{tab.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          )}
        </div>
      )}

      {/* Content Area - Better Aspect Ratio for Charts */}
      <div className={`${title || subtitle || actions || tabs ? '' : ''}`}>
        <div className="h-[32rem] w-full">
          {loading && loadingComponent ? loadingComponent : children}
        </div>
      </div>
    </Card>
  );
};

export default PlotCard;
